import React, { useEffect, useRef, useState, useCallback } from "react";
import Webcam from "react-webcam";
import { Box, Modal, CircularProgress, Typography } from "@mui/material";
import { CloseOutlined } from "@mui/icons-material";

import * as faceLandmarksDetection from "@tensorflow-models/face-landmarks-detection";
import "@tensorflow/tfjs-backend-webgl";
import CustomCircleChart from "../../../../../common/components/CustomCircleChart";

// --- Constants for Liveness Detection ---
const MAX_SCORE = 100;
const EAR_THRESHOLD = 0.25; // Eye-Aspect-Ratio threshold for blink detection
const EAR_CONSEC_FRAMES = 2; // Number of consecutive frames below threshold for a blink
const HEAD_TURN_THRESHOLD_X = 0.08; // Horizontal movement threshold for head turn
const SMILE_THRESHOLD = 0.4; // Mouth-Aspect-Ratio threshold for smile

// --- Keypoint indices from MediaPipe Face Mesh ---
const LEFT_EYE_INDICES = [33, 160, 158, 133, 153, 144];
const RIGHT_EYE_INDICES = [362, 385, 387, 263, 373, 380];
const MOUTH_INDICES = {
  top: 13,
  bottom: 14,
  left: 61,
  right: 291,
};
const NOSE_TIP_INDEX = 1;

// --- Component Props ---
interface LivelinessCheckModalProps {
  showModal: boolean;
  onCloseModal: () => void;
  _setScore: React.Dispatch<React.SetStateAction<number>>;
}

// --- Main Component ---
export default function LivelinessCheckModal({
  showModal,
  onCloseModal,
  _setScore,
}: LivelinessCheckModalProps) {
  const webcamRef = useRef<Webcam>(null);
  const detectorRef =
    useRef<faceLandmarksDetection.FaceLandmarksDetector | null>(null);
  const requestRef = useRef<number>();
  const livenessState = useRef({
    isLoading: true,
    instruction: "Loading model...",
    faceDetected: false,
    blinkDetected: false,
    headTurnDetected: false,
    smileDetected: false,
    blinkFrameCounter: 0,
    initialNoseX: 0,
  });

  const [score, setScore] = useState(0);
  const [displayState, setDisplayState] = useState({
    isLoading: true,
    instruction: "Loading model...",
    faceDetected: false,
  });

  // --- Liveness Logic ---

  const calculateDistance = (
    p1: faceLandmarksDetection.Keypoint,
    p2: faceLandmarksDetection.Keypoint
  ) => Math.hypot(p1.x - p2.x, p1.y - p2.y);

  const calculateEAR = (
    eyeIndices: number[],
    keypoints: faceLandmarksDetection.Keypoint[]
  ) => {
    const p1 = keypoints[eyeIndices[0]];
    const p2 = keypoints[eyeIndices[1]];
    const p3 = keypoints[eyeIndices[2]];
    const p4 = keypoints[eyeIndices[3]];
    const p5 = keypoints[eyeIndices[4]];
    const p6 = keypoints[eyeIndices[5]];

    const verticalDist = calculateDistance(p2, p6) + calculateDistance(p3, p5);
    const horizontalDist = 2 * calculateDistance(p1, p4);

    return verticalDist / horizontalDist;
  };

  const calculateMAR = (keypoints: faceLandmarksDetection.Keypoint[]) => {
    const mouthLeft = keypoints[MOUTH_INDICES.left];
    const mouthRight = keypoints[MOUTH_INDICES.right];
    const mouthTop = keypoints[MOUTH_INDICES.top];
    const mouthBottom = keypoints[MOUTH_INDICES.bottom];

    const mouthWidth = calculateDistance(mouthLeft, mouthRight);
    const mouthHeight = calculateDistance(mouthTop, mouthBottom);

    return mouthHeight / mouthWidth;
  };

  const updateScore = useCallback(
    (points: number) => {
      setScore((prevScore) => {
        const newScore = Math.min(prevScore + points, MAX_SCORE);
        _setScore(newScore);
        return newScore;
      });
    },
    [_setScore]
  );

  const runDetection = useCallback(async () => {
    const video = webcamRef.current?.video;
    const detector = detectorRef.current;

    if (!detector || !video || video.readyState !== 4) {
      requestRef.current = requestAnimationFrame(runDetection);
      return;
    }

    const faces = await detector.estimateFaces(video, {
      flipHorizontal: false,
    });

    if (faces.length > 0) {
      const keypoints = faces[0].keypoints;
      const boxWidth = faces[0].box.xMax - faces[0].box.xMin;

      if (!livenessState.current.faceDetected) {
        livenessState.current.faceDetected = true;
        livenessState.current.initialNoseX = keypoints[NOSE_TIP_INDEX].x;
        livenessState.current.instruction = "Please blink your eyes.";
        updateScore(25); // Initial face detection score
      }

      // 1. Blink Detection
      if (!livenessState.current.blinkDetected) {
        const leftEAR = calculateEAR(LEFT_EYE_INDICES, keypoints);
        const rightEAR = calculateEAR(RIGHT_EYE_INDICES, keypoints);
        const avgEAR = (leftEAR + rightEAR) / 2;

        if (avgEAR < EAR_THRESHOLD) {
          livenessState.current.blinkFrameCounter++;
        } else {
          if (livenessState.current.blinkFrameCounter >= EAR_CONSEC_FRAMES) {
            livenessState.current.blinkDetected = true;
            livenessState.current.instruction = "Great! Now please smile.";
            updateScore(25);
          }
          livenessState.current.blinkFrameCounter = 0;
        }
      }
      // 2. Smile Detection
      else if (!livenessState.current.smileDetected) {
        const mar = calculateMAR(keypoints);
        if (mar > SMILE_THRESHOLD) {
          livenessState.current.smileDetected = true;
          livenessState.current.instruction =
            "Awesome! Now turn your head slightly.";
          updateScore(25);
        }
      }
      // 3. Head Turn Detection
      else if (!livenessState.current.headTurnDetected) {
        const currentNoseX = keypoints[NOSE_TIP_INDEX].x;
        const headMoveDelta =
          Math.abs(currentNoseX - livenessState.current.initialNoseX) /
          boxWidth;

        if (headMoveDelta > HEAD_TURN_THRESHOLD_X) {
          livenessState.current.headTurnDetected = true;
          livenessState.current.instruction = "Liveness check complete!";
          updateScore(25);
        }
      }
    } else {
      livenessState.current.faceDetected = false;
      livenessState.current.instruction =
        "No face detected. Please position your face in the center.";
    }

    setDisplayState({
      isLoading: livenessState.current.isLoading,
      instruction: livenessState.current.instruction,
      faceDetected: livenessState.current.faceDetected,
    });

    requestRef.current = requestAnimationFrame(runDetection);
  }, [updateScore]);

  // --- Effects ---

  useEffect(() => {
    if (showModal) {
      const loadModelAndStartDetection = async () => {
        // Ensure UI shows loading state
        setDisplayState((prev) => ({
          ...prev,
          isLoading: true,
          instruction: "Loading model...",
        }));
        try {
          const model =
            faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
          const detectorConfig: faceLandmarksDetection.MediaPipeFaceMeshMediaPipeModelConfig =
            {
              runtime: "mediapipe",
              refineLandmarks: true,
              maxFaces: 1,
              solutionPath:
                "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/",
            };
          detectorRef.current = await faceLandmarksDetection.createDetector(
            model,
            detectorConfig
          );
          livenessState.current.isLoading = false;
          // Start detection loop on success, which will update the UI
          requestRef.current = requestAnimationFrame(runDetection);
        } catch (error) {
          console.error("Error loading face model:", error);
          // Update UI to show error state immediately
          setDisplayState((prev) => ({
            ...prev,
            isLoading: false,
            instruction: "Error loading model. Please try again.",
          }));
        }
      };

      const loadModel = async () => {
        try {
          const model =
            faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
          const config = {
            runtime: "tfjs", // Use the TFJS backend instead of MediaPipe WASM
            solutionPath:
              "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/mediapipe-face-mesh.tflite",
            refineLandmarks: false, // set as needed for iris tracking
            // modelComplexity: 1,     // Use a lighter model complexity
          };
          return await faceLandmarksDetection.createDetector(
            model,
            config as faceLandmarksDetection.MediaPipeFaceMeshMediaPipeModelConfig
          );
        } catch (error) {
          console.error("Error loading face model:", error);
          // Update UI to show error state immediately
          setDisplayState((prev) => ({
            ...prev,
            isLoading: false,
            instruction: "Error loading model. Please try again.",
          }));
        }
      };

      loadModel().then((detector) => {
        detectorRef.current = detector || null;
        setDisplayState({
          isLoading: false,
          instruction: "Loading model...",
          faceDetected: false,
        });

        livenessState.current.isLoading = false;
        // Start detection loop on success, which will update the UI
        requestRef.current = requestAnimationFrame(runDetection);
      });

      // loadModelAndStartDetection();
    }

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
      if (detectorRef.current) {
        detectorRef.current.dispose();
        detectorRef.current = null;
      }
    };
  }, [showModal, runDetection]);

  useEffect(() => {
    if (score === MAX_SCORE) {
      setTimeout(() => {
        onCloseModal();
      }, 1000); // Close modal 1 second after completion
    }
  }, [score, onCloseModal]);

  // --- Render ---

  return (
    <Modal
      open={showModal}
      onClose={onCloseModal}
      aria-labelledby="liveliness-check-modal"
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          textAlign: "center",
          bgcolor: "background.paper",
          p: 4,
          borderRadius: 2,
          boxShadow: 24,
          position: "relative",
          maxWidth: "90vw",
          maxHeight: "90vh",
        }}
      >
        <CloseOutlined
          onClick={onCloseModal}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            cursor: "pointer",
          }}
        />
        <Typography variant="h5" component="h2" gutterBottom>
          Liveliness Check
        </Typography>

        <Box
          sx={{ position: "relative", margin: "auto" }}
          className="webcam-container"
        >
          <Webcam
            ref={webcamRef}
            audio={false}
            mirrored
            width={640}
            height={480}
            style={{ borderRadius: 8 }}
            videoConstraints={{ facingMode: "user" }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: displayState.isLoading ? "flex" : "none",
              flexDirection: "column",
              alignItems: "center",
              color: "white",
            }}
          >
            <CircularProgress color="inherit" />
            <Typography sx={{ mt: 2 }}>{displayState.instruction}</Typography>
          </Box>
        </Box>

        <Typography variant="h6" sx={{ mt: 2 }}>
          {displayState.faceDetected
            ? "✅ Face Detected"
            : "❌ No Face Detected"}
        </Typography>
        <Typography variant="body1" sx={{ mt: 1, minHeight: "24px" }}>
          {displayState.instruction}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CustomCircleChart percentage={score} />
        </Box>
      </Box>
    </Modal>
  );
}
